import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';

function TestsProcedures() {
  const { letter } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [testsProcedures, setTestsProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tghNews, setTghNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  const fetchNews = async () => {
    setNewsLoading(true);
    try {
      const response = await axios.get('/api/news');
      const news = Array.isArray(response.data)
        ? response.data
        : response.data?.news || [];
      news.sort((a, b) => new Date(b.date) - new Date(a.date));
      setTghNews(news);
    } catch (error) {
      console.error('Error fetching news:', error);
      setTghNews([]);
    } finally {
      setNewsLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get('search');

    if (searchParam) {
      setSearchQuery(searchParam);
      handleSearchDirect(searchParam);
    } else if (letter) {
      fetchTestsProceduresByLetter(letter);
    }
  }, [letter, location.search]);

  const handleSearchDirect = async (query) => {
    if (!query.trim()) return;

    try {
      setLoading(true);
      const response = await axios.get(`/api/tests-procedures?search=${encodeURIComponent(query)}`);
      setSearchResults(response.data.tests_procedures);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error searching tests & procedures:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTestsProceduresByLetter = async (ltr) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/tests-procedures?letter=${ltr}`);
      setTestsProcedures(response.data.tests_procedures);
    } catch (error) {
      console.error('Error fetching tests & procedures:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get('search') || searchQuery;
    
    if (!searchParam.trim()) return;

    try {
      const response = await axios.get(`/api/tests-procedures?search=${encodeURIComponent(searchParam)}`);
      setSearchResults(response.data.tests_procedures);
      setShowSearchResults(true);
      setSearchQuery(searchParam);
    } catch (error) {
      console.error('Error searching tests & procedures:', error);
    }
  };

  const handleLetterClick = (ltr) => {
    navigate(`/tests-procedures/${ltr}`);
    setShowSearchResults(false);
    setSearchQuery('');
    window.scrollTo(0, 0);
  };

  // Group tests/procedures by first letter(s) for display
  const groupTestsProcedures = (list) => {
    const grouped = {};
    list.forEach(item => {
      const key = item.name.substring(0, 2).toUpperCase();
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(item);
    });
    return grouped;
  };

  const groupedTestsProcedures = groupTestsProcedures(testsProcedures);
  const groupedSearchResults = groupTestsProcedures(searchResults);

  return (
    <>
      {/* Hero Section - Dark Blue Background */}
      <section className="tgh-tests-procedures-hero py-5" style={{ backgroundColor: '#003d82', color: '#fff' }}>
        <div className="container">
          <nav aria-label="breadcrumb" className="mb-3">
            <ol className="breadcrumb" style={{ backgroundColor: 'transparent', padding: 0 }}>
              <li className="breadcrumb-item"><Link to="/" style={{ color: '#fff' }}>Home</Link></li>
              <li className="breadcrumb-item"><Link to="/tests-procedures" style={{ color: '#fff' }}>Tests & Procedures</Link></li>
              {letter && <li className="breadcrumb-item active" style={{ color: '#fff' }}>Begins with '{letter}'</li>}
            </ol>
          </nav>

          <h1 className="display-4 mb-3" style={{ fontFamily: 'Lora, serif', fontWeight: 'bold' }}>
            Tests & Procedures
          </h1>
          <p className="lead mb-4" style={{ fontSize: '1.1rem' }}>
            Easy-to-understand information about medical tests and procedures
          </p>

          <div className="row mt-4">
            <div className="col-lg-6">
              <form onSubmit={handleSearch} className="mb-4">
                <div className="input-group input-group-lg">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search tests & procedures"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ borderRadius: '4px 0 0 4px' }}
                  />
                  <button className="btn btn-light" type="submit" style={{ borderRadius: '0 4px 4px 0' }}>
                    <i className="bi bi-search"></i>
                  </button>
                </div>
              </form>
            </div>
            <div className="col-lg-6">
              <div>
                <p className="mb-2" style={{ fontSize: '0.9rem', fontWeight: 600 }}>
                  Find tests & procedures by first letter
                </p>
                <div className="d-flex flex-wrap gap-2">
                  {alphabet.map((ltr) => (
                    <button
                      key={ltr}
                      type="button"
                      className={`btn ${letter === ltr ? 'btn-light' : 'btn-outline-light'}`}
                      onClick={() => handleLetterClick(ltr)}
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        fontWeight: '600',
                        border: letter === ltr ? '2px solid #fff' : '2px solid rgba(255,255,255,0.5)'
                      }}
                    >
                      {ltr}
                    </button>
                  ))}
                  <button
                    type="button"
                    className={`btn ${letter === '#' ? 'btn-light' : 'btn-outline-light'}`}
                    onClick={() => handleLetterClick('#')}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      fontWeight: '600',
                      border: letter === '#' ? '2px solid #fff' : '2px solid rgba(255,255,255,0.5)'
                    }}
                  >
                    #
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section - White Background */}
      <section className="py-5" style={{ backgroundColor: '#fff', minHeight: '60vh' }}>
        <div className="container">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : showSearchResults ? (
            <>
              <h2 className="mb-4">Search Results for "{searchQuery}"</h2>
              {searchResults.length === 0 ? (
                <p>No tests or procedures found matching your search.</p>
              ) : (
                <div className="row">
                  <div className="col-lg-9">
                    {Object.keys(groupedSearchResults).sort().map((key) => (
                      <div key={key} className="mb-5">
                        <h3 className="mb-3" style={{ fontSize: '2rem', fontWeight: 'bold' }}>{key}</h3>
                        <ul className="list-unstyled">
                          {groupedSearchResults[key].map((item) => (
                            <li key={item.id} className="mb-2">
                              {item.alias ? (
                                <>
                                  <span>{item.alias}</span>
                                  {' '}
                                  <Link to={`/tests-procedures/detail/${item.id}`} style={{ color: '#0066cc' }}>
                                    See {item.name}
                                  </Link>
                                </>
                              ) : (
                                <Link to={`/tests-procedures/detail/${item.id}`} style={{ color: '#0066cc' }}>
                                  {item.name}
                                </Link>
                              )}
                            </li>
                          ))}
                        </ul>
                        <a 
                          href="#top" 
                          onClick={(e) => {
                            e.preventDefault();
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          style={{ color: '#0066cc', fontSize: '0.9rem', textDecoration: 'none', cursor: 'pointer' }}
                        >
                          Back to top <i className="bi bi-arrow-up"></i>
                        </a>
                      </div>
                    ))}
                  </div>
                  <div className="col-lg-3">
                    <div className="border-start ps-4" style={{ borderColor: '#e0e0e0' }}>
                      <h6 className="mb-2">TGH News</h6>
                      <p className="text-muted small mb-3">
                        Latest news and updates from Tema General Hospital.
                      </p>
                      {newsLoading ? (
                        <div className="text-center py-2">
                          <div className="spinner-border spinner-border-sm text-primary" role="status" />
                          <div className="small text-muted mt-2">Loading news...</div>
                        </div>
                      ) : tghNews.length === 0 ? (
                        <p className="text-muted small mb-0">No news articles available at this time.</p>
                      ) : (
                        <ul className="list-unstyled small" style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '0.25rem' }}>
                          {tghNews.map((post) => (
                            <li key={post.id} className="mb-3 pb-2" style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <Link
                                to={`/news/${post.id}`}
                                style={{ color: '#0066cc', textDecoration: 'none', fontWeight: 600 }}
                              >
                                {post.title}
                              </Link>
                              {post.date && (
                                <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.15rem' }}>
                                  {formatDate(post.date)}
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {letter && (
                <div className="row">
                  <div className="col-lg-9">
                    {testsProcedures.length === 0 ? (
                      <div className="mb-5">
                        <h3 className="mb-3" style={{ fontSize: '2rem', fontWeight: 'bold' }}>{letter}</h3>
                        <p>No tests or procedures found starting with '{letter}'.</p>
                      </div>
                    ) : (
                      Object.keys(groupedTestsProcedures).sort().map((key) => (
                        <div key={key} className="mb-5">
                          <h3 className="mb-3" style={{ fontSize: '2rem', fontWeight: 'bold' }}>{key}</h3>
                          <ul className="list-unstyled">
                            {groupedTestsProcedures[key].map((item) => (
                              <li key={item.id} className="mb-2">
                                {item.alias ? (
                                  <>
                                    <span>{item.alias}</span>
                                    {' '}
                                    <Link to={`/tests-procedures/detail/${item.id}`} style={{ color: '#0066cc' }}>
                                      See {item.name}
                                    </Link>
                                  </>
                                ) : (
                                  <Link to={`/tests-procedures/detail/${item.id}`} style={{ color: '#0066cc' }}>
                                    {item.name}
                                  </Link>
                                )}
                              </li>
                            ))}
                          </ul>
                          <a 
                            href="#top" 
                            onClick={(e) => {
                              e.preventDefault();
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            style={{ color: '#0066cc', fontSize: '0.9rem', textDecoration: 'none', cursor: 'pointer' }}
                          >
                            Back to top <i className="bi bi-arrow-up"></i>
                          </a>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="col-lg-3">
                    <div className="border-start ps-4" style={{ borderColor: '#e0e0e0' }}>
                      <h6 className="mb-2">TGH News</h6>
                      <p className="text-muted small mb-3">
                        Latest news and updates from Tema General Hospital.
                      </p>
                      {newsLoading ? (
                        <div className="text-center py-2">
                          <div className="spinner-border spinner-border-sm text-primary" role="status" />
                          <div className="small text-muted mt-2">Loading news...</div>
                        </div>
                      ) : tghNews.length === 0 ? (
                        <p className="text-muted small mb-0">No news articles available at this time.</p>
                      ) : (
                        <ul className="list-unstyled small" style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '0.25rem' }}>
                          {tghNews.map((post) => (
                            <li key={post.id} className="mb-3 pb-2" style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <Link
                                to={`/news/${post.id}`}
                                style={{ color: '#0066cc', textDecoration: 'none', fontWeight: 600 }}
                              >
                                {post.title}
                              </Link>
                              {post.date && (
                                <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.15rem' }}>
                                  {formatDate(post.date)}
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
}

export default TestsProcedures;
